<script lang="ts">
    import ZentitySelect from '../../_common/z/zentity-select.svelte'
    import { createEventDispatcher, onMount } from 'svelte'
    import Fa from 'svelte-fa'
    import { faArrowLeft, faCheck, faFloppyDisk, faPrint, faCircleCheck, faClock } from '@fortawesome/free-solid-svg-icons'
    import Zact from '../../_common/z/zact.svelte'
    import Zbutton from '../../_common/z/zbutton.svelte'
    import Zframe from '../../_common/z/zframe.svelte'
    import Zinput from '../../_common/z/zinput.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import Popover from '../../_common/popover.svelte'

    import FormFill from './form-fill.svelte'
    import { printFormV2 } from './form-v2-print.helper'
    import { buildContextSnapshot, isDocumentFrozen, resolveFillContext, type ContextSnapshot } from './context-snapshot'
    import type { FormV2Form } from '../../../modules/toolsApi/forms-v2/form-v2-forms/form-v2-form'
    import type { FormV2Template } from '../../../modules/toolsApi/forms-v2/form-v2-templates/form-v2-template'
    import type { PlacedBlock } from '../types/placed-block'
    import type { DocumentHeaderFooter } from '../types/document-header-footer'
    import { createDefaultHeaderFooter } from '../types/document-header-footer'
    import { FormTemplateTypeEnum, FormTemplateTypeFriendlyEnum } from '../../../modules/toolsApi/forms/form-templates/form-template-item-type.enum'

    const dispatch = createEventDispatcher()

    const DRAFT = 60
    const COMPLETED = 100
    const SIGNING = 105
    const SIGNED = 110

    const INVOICE_TAGS = [
        '{compra}',
        '{compraporextenso}',
        '{pagamentoporextenso}',
        '{valordacompraporextenso}',
        '{valordopagamentoporextenso}',
        '{datacompra}',
        '{pagamento}',
        '{databaixa}',
        '{descricao}',
        '{vendedor}',
    ]

    export let formV2FormId: string | null = null
    export let personId: string | null = null
    export let invoiceId: string | null = null
    export let billId: string | null = null
    export let view: boolean = false
    /** Filtro inicial de categoria — mesma ideia de customer-service-form.svelte's `types`, permite
     * que o chamador já abra a tela com o multiselect de categoria pré-preenchido. */
    export let types: FormTemplateTypeEnum[] = []

    let show = false
    let saving = false
    let generateLinkAfterSave = false

    let formFillRef: any
    let selectedAttendant: any = null
    let selectedPerson: any = null
    let selectedTemplateId: string = ''
    let templates: Pick<FormV2Template, 'formV2TemplateId' | 'title' | 'status' | 'category'>[] = []
    let filteredTemplates: Pick<FormV2Template, 'formV2TemplateId' | 'title' | 'status' | 'category'>[] = []
    const categoryOptions = Object.keys(FormTemplateTypeEnum).map((key) => ({ value: key, label: FormTemplateTypeFriendlyEnum[key] }))
    let blocks: PlacedBlock[] = []
    let headerFooter: DocumentHeaderFooter = createDefaultHeaderFooter()
    let formTitle = ''
    let initialAnswers: Record<string, any> = {}
    let isView = false
    let formStatus = 0
    // Campos do cadastro da pessoa exigidos pelo template selecionado (ver "Campos obrigatórios da
    // pessoa" em Propriedades) — checado direto contra selectedPerson em validateSidebar(),
    // independente de quais blocos existem no template.
    let templateRequiredFields: string[] = []
    const REQUIRED_FIELD_LABELS: Record<string, string> = { document: 'CPF/CNPJ', email: 'E-mail', address: 'Endereço' }
    let invoiceRequired = false
    let invoice: any = null
    let bill: any = null
    /** Contexto congelado no documento; ver context-snapshot.ts. */
    let contextSnapshot: ContextSnapshot | null = null

    // Aviso antecipado: sem isso o usuário só descobria o cadastro incompleto no validateSidebar(),
    // depois de preencher o formulário inteiro. Não bloqueia nada — o barramento de verdade continua
    // no salvamento.
    let missingPersonFields: string[] = []
    // Evita refazer a busca a cada invalidação reativa: só reconsulta quando a pessoa ou a lista de
    // campos exigidos muda de fato.
    let checkedRequiredFieldsKey = ''

    $: checkPersonRequiredFields(selectedPerson?.personId, templateRequiredFields)

    async function checkPersonRequiredFields(personIdToCheck: string | undefined, requiredFields: string[]) {
        if (isView || !personIdToCheck || !requiredFields?.length) {
            missingPersonFields = []
            checkedRequiredFieldsKey = ''
            return
        }
        const key = personIdToCheck + '|' + requiredFields.join(',')
        if (key === checkedRequiredFieldsKey) return
        checkedRequiredFieldsKey = key
        try {
            // Busca o cadastro completo em vez de usar selectedPerson: vindo do seletor ou do
            // loadForm ele traz só personId/name, e os campos apareceriam como faltando sempre.
            // Não reatribui selectedPerson de propósito — ele é dependência deste bloco reativo.
            const person = await window.api.events.persons.getOne(personIdToCheck)
            missingPersonFields = requiredFields.filter((field) => !person?.[field])
        } catch (error) {
            // O aviso é conveniência: se a consulta falhar, segue em silêncio em vez de atrapalhar o
            // preenchimento. O validateSidebar() ainda barra no salvamento.
            missingPersonFields = []
        }
    }

    onMount(async () => {
        await loadTemplates()
        if (formV2FormId) {
            await loadForm(formV2FormId)
        } else {
            await loadExternalDocument(invoiceId, billId)
        }
        if (!selectedPerson) {
            await prefillPerson()
        }
    })

    async function prefillPerson() {
        if (!personId) return
        try {
            // Guarda o cadastro inteiro: as tags da pessoa dependem dele, e é o que vai para o
            // contextSnapshot no salvamento.
            selectedPerson = (await window.api.events.persons.getOne(personId)) ?? null
        } catch (error) {
            window.error(error)
        }
    }

    /**
     * Documento editável (< 100) busca o cadastro atual; congelado lê o que ficou no snapshot — a
     * mesma regra que vale para todos os caminhos de renderização (ver context-snapshot.ts).
     */
    async function resolvePersonForForm(form: FormV2Form) {
        if (!form.personId) return null
        if (isDocumentFrozen(form.status)) {
            return contextSnapshot?.person ?? { personId: form.personId, name: form.personName }
        }
        try {
            return (await window.api.events.persons.getOne(form.personId)) ?? null
        } catch (error) {
            return { personId: form.personId, name: form.personName }
        }
    }

    async function loadExternalDocument(docInvoiceId: string | null, docBillId: string | null) {
        try {
            if (docInvoiceId) {
                invoice = await window.api.pos.invoices.getOne(docInvoiceId)
                // Mesmo motivo da fonte "venda" do ZentitySelect: o getOne também não traz o nome do vendedor, só o
                // userId — sem resolver aqui, a tag {vendedor} sai '[-]' ao reabrir um formulário.
                if (invoice?.services) {
                    invoice.services = await window.api.utils.utils.assignUsersFromObject(invoice.services, false, 'userId', 'user', true)
                }
            } else if (docBillId) {
                bill = await window.api.financials.bills.getOne(docBillId)
            }
        } catch (error) {
            window.error(error)
        }
    }

    async function loadTemplates() {
        try {
            templates = await window.api.tools.formsV2.formTemplates.getAll()
            onTypesChange()
        } catch (error) {
            window.error(error)
        }
    }

    function onTypesChange() {
        if (!types) types = []
        const selected: string[] = types
        filteredTemplates =
            selected.length === 0 ? templates.filter((t) => t.status === 100) : templates.filter((t) => t.status === 100 && selected.includes(t.category ?? ''))
    }

    function checkExternalDocument(blocksToCheck: PlacedBlock[]) {
        invoiceRequired = false
        // Preserva invoice/bill quando vieram de invoiceId/billId (props) — a associação é
        // explícita pelo chamador, independe do template atual referenciar tags de venda/conta.
        if (!invoiceId && !billId) {
            invoice = null
            bill = null
        }
        for (const block of blocksToCheck) {
            if (block.type === 'label') {
                const text: string = block.data?.text ?? ''
                if (INVOICE_TAGS.some((tag) => text.includes(tag))) {
                    invoiceRequired = true
                    return
                }
            }
        }
    }

    async function loadForm(id: string) {
        try {
            show = true
            const form: FormV2Form = await window.api.tools.formsV2.forms.getOne(id)
            formV2FormId = form.formV2FormId
            selectedTemplateId = form.formV2TemplateId
            selectedAttendant = form.attendantId ? { userId: form.attendantId, userName: form.attendantName } : null
            contextSnapshot = form.document?.contextSnapshot ?? null
            // Cadastro completo, não só {personId, name}: as tags da pessoa ({email}, {documento},
            // {endereco}...) leem daqui, e reduzir o objeto as deixava em branco até o próximo
            // salvamento. Congelado, isso gravaria o vazio no snapshot.
            selectedPerson = await resolvePersonForForm(form)
            formTitle = form.title
            const snap = form.document?.templateSnapshot
            blocks = snap?.blocks ?? []
            headerFooter = snap?.headerFooter ?? createDefaultHeaderFooter()
            initialAnswers = form.document?.answers ?? {}
            formStatus = form.status
            isView = view || form.status >= COMPLETED
            checkExternalDocument(blocks)
            await loadExternalDocument(form.document?.invoiceId ?? invoiceId, form.document?.billId ?? billId)
            await loadTemplateRequiredFields(form.formV2TemplateId)
            console.log('form', form)
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function onTemplateChange() {
        if (!selectedTemplateId) {
            blocks = []
            headerFooter = createDefaultHeaderFooter()
            formTitle = ''
            templateRequiredFields = []
            return
        }
        try {
            show = true
            const tpl: FormV2Template = await window.api.tools.formsV2.formTemplates.getOne(selectedTemplateId)
            blocks = tpl.document?.blocks ?? []
            headerFooter = tpl.document?.headerFooter ?? createDefaultHeaderFooter()
            formTitle = tpl.title
            templateRequiredFields = tpl.requiredFields ?? []
            initialAnswers = {}
            checkExternalDocument(blocks)
            await loadExternalDocument(invoiceId, billId)
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function loadTemplateRequiredFields(templateId: string) {
        if (!templateId) {
            templateRequiredFields = []
            return
        }
        try {
            const tpl: FormV2Template = await window.api.tools.formsV2.formTemplates.getOne(templateId)
            templateRequiredFields = tpl.requiredFields ?? []
        } catch (error) {
            templateRequiredFields = []
        }
    }

    async function validateSidebar() {
        if (!selectedTemplateId) {
            window.error('Selecione o formulário')
            return false
        }
        if (!selectedAttendant) {
            window.error('Selecione quem está preenchendo')
            return false
        }
        if (!selectedPerson) {
            window.error('Selecione a pessoa vinculada')
            return false
        }
        if (invoiceRequired && !invoice && !bill) {
            window.error('Selecione a venda vinculada')
            return false
        }
        if (templateRequiredFields.length > 0) {
            // Recarrega o cadastro no momento de salvar, em vez de conferir o selectedPerson que já
            // está em memória: ele fica defasado se a pessoa for completada em outra tela depois
            // deste formulário ser aberto, e ao abrir um formulário existente ele nasce só com
            // personId/name (ver loadForm) — sem isso, campos como e-mail/endereço apareceriam como
            // faltando mesmo já estando preenchidos. Reatribuir também mantém as tags ({email},
            // {endereco}...) resolvendo com o dado atual.
            try {
                selectedPerson = await window.api.events.persons.getOne(selectedPerson.personId)
            } catch (error) {
                window.error(error)
                return false
            }
            const missing = templateRequiredFields.filter((field) => !selectedPerson?.[field])
            if (missing.length > 0) {
                window.error('Para continuar, complete o cadastro do cliente:\n' + missing.map((field) => REQUIRED_FIELD_LABELS[field] ?? field).join(', '))
                return false
            }
        }
        return true
    }

    async function persistForm(docFromFill: any, status: number): Promise<boolean> {
        // Congela o contexto das tags {} junto do documento, ao lado do templateSnapshot. Só o editor
        // logado grava, e só enquanto o documento é editável: de 100 em diante todo mundo lê o que
        // ficou aqui, para a tela, a impressão da equipe e o PDF assinado nunca divergirem.
        // Ver context-snapshot.ts para a regra completa.
        const document = {
            ...docFromFill,
            invoiceId: invoice?.invoiceId ?? null,
            billId: bill?.billId ?? null,
            contextSnapshot: buildContextSnapshot({ person: selectedPerson, invoice, bill, attendant: selectedAttendant }, contextSnapshot),
        }
        contextSnapshot = document.contextSnapshot
        try {
            const dto = {
                formV2TemplateId: selectedTemplateId,
                attendantId: selectedAttendant.userId,
                attendantName: selectedAttendant.userName,
                personId: selectedPerson.personId,
                personName: selectedPerson.name,
                title: formTitle,
                status,
                document,
            }
            if (formV2FormId) {
                const saved = await window.api.tools.formsV2.forms.update(formV2FormId, { ...dto, status })
                // O servidor reconcilia as tabelas dinâmicas antes de gravar (linhas e assinaturas que
                // entraram por outro caminho enquanto esta tela estava aberta). Realimenta o que ficou
                // salvo de fato, senão a assinatura preservada só apareceria ao reabrir o documento.
                if (saved?.document?.answers) formFillRef?.setAnswers(saved.document.answers)
            } else {
                const result = await window.api.tools.formsV2.forms.create(dto)
                formV2FormId = result.formV2FormId
            }
            return true
        } catch (error) {
            window.error(error)
            return false
        }
    }

    function extractExtensionFromDataUrl(dataUrl: string): string {
        const mime = dataUrl.match(/^data:([^;]+);/)?.[1] ?? ''
        const map: Record<string, string> = {
            'image/png': 'png',
            'image/jpeg': 'jpg',
            'image/gif': 'gif',
            'image/webp': 'webp',
            'image/bmp': 'bmp',
            'application/pdf': 'pdf',
        }
        return map[mime] ?? 'bin'
    }

    async function uploadBase64ToS3(dataUrl: string, extension: string): Promise<string> {
        const { url } = await window.api.events.files.uploadToSignedLink({ content: dataUrl, extension, folderName: 'forms-v2' })
        return url
    }

    async function uploadFilesInDocument(doc: any): Promise<any> {
        const answers: Record<string, any> = {}
        for (const blockId of Object.keys(doc.answers)) {
            const value = doc.answers[blockId]
            const block = doc.templateSnapshot.blocks.find((b: any) => b.id === blockId)
            if (!block || !value) {
                answers[blockId] = value
                continue
            }

            if (block.type === 'sign' && typeof value === 'string' && value.startsWith('data:')) {
                answers[blockId] = await uploadBase64ToS3(value, 'png')
            } else if (block.type === 'image_annotation' && Array.isArray(value)) {
                answers[blockId] = await Promise.all(
                    value.map(async (item: any) => {
                        if (!item.dataUrl?.startsWith('data:')) return item
                        // Extensão derivada do próprio conteúdo, não fixa: o editor de marcação passou
                        // a achatar a foto em JPEG, e gravar bytes JPEG num arquivo .png enganaria
                        // qualquer coisa que confie na extensão.
                        return { ...item, dataUrl: await uploadBase64ToS3(item.dataUrl, extractExtensionFromDataUrl(item.dataUrl)) }
                    }),
                )
            } else if (block.type === 'file_upload' && Array.isArray(value)) {
                answers[blockId] = await Promise.all(
                    value.map(async (item: any) => {
                        if (!item.dataUrl?.startsWith('data:')) return item
                        const ext = item.name.split('.').pop()?.toLowerCase() || 'bin'
                        return { ...item, dataUrl: await uploadBase64ToS3(item.dataUrl, ext) }
                    }),
                )
            } else if (block.type === 'before_after' && value && typeof value === 'object') {
                const { before, after } = value
                answers[blockId] = {
                    before: before?.src?.startsWith('data:')
                        ? { ...before, src: await uploadBase64ToS3(before.src, extractExtensionFromDataUrl(before.src)) }
                        : before,
                    after: after?.src?.startsWith('data:')
                        ? { ...after, src: await uploadBase64ToS3(after.src, extractExtensionFromDataUrl(after.src)) }
                        : after,
                }
            } else {
                answers[blockId] = value
            }
        }

        // Upload do logo do cabeçalho se ainda for base64
        let updatedHeaderFooter = doc.templateSnapshot?.headerFooter
        if (updatedHeaderFooter?.header?.logo?.startsWith('data:')) {
            const ext = extractExtensionFromDataUrl(updatedHeaderFooter.header.logo)
            const logoUrl = await uploadBase64ToS3(updatedHeaderFooter.header.logo, ext)
            updatedHeaderFooter = { ...updatedHeaderFooter, header: { ...updatedHeaderFooter.header, logo: logoUrl } }
            // Atualiza o estado local para que re-saves usem a URL S3
            headerFooter = updatedHeaderFooter
        }

        return {
            ...doc,
            answers,
            templateSnapshot: { ...doc.templateSnapshot, headerFooter: updatedHeaderFooter },
        }
    }

    async function onSaveDraft() {
        if (saving || !formFillRef) return
        if (!(await validateSidebar())) return
        saving = true
        try {
            let doc = formFillRef.buildDocument('draft')
            doc = await uploadFilesInDocument(doc)
            formFillRef.setAnswers(doc.answers)
            const ok = await persistForm(doc, DRAFT)
            if (ok) {
                dispatch('change', { formV2FormId, openLink: generateLinkAfterSave, completed: false })
            }
        } catch (error) {
            window.error(error)
        } finally {
            saving = false
        }
    }

    async function onSubmit() {
        if (saving || !formFillRef) return
        if (!(await validateSidebar())) return
        if (!formFillRef.validate()) return
        saving = true
        try {
            let doc = formFillRef.buildDocument('submitted')
            doc = await uploadFilesInDocument(doc)
            formFillRef.setAnswers(doc.answers)
            const ok = await persistForm(doc, COMPLETED)
            if (ok) {
                dispatch('change', { formV2FormId, openLink: generateLinkAfterSave, completed: true })
            }
        } catch (error) {
            window.error(error)
        } finally {
            saving = false
        }
    }

    function onBack() {
        dispatch('cancel')
    }

    // Só alterna a tela local para o modo de preenchimento (destrava os campos) — não salva nem
    // navega para lugar nenhum. O documento só volta a status "rascunho" de fato quando a pessoa
    // salvar/concluir normalmente a partir daqui.
    function backToFilling() {
        isView = false
    }

    async function printCurrentForm() {
        if (!formV2FormId) return
        try {
            show = true
            await printFormV2(formV2FormId)
        } finally {
            show = false
        }
    }
</script>

<Zact>
    <Zbutton color="secondary" icon={faArrowLeft} label="Voltar" on:click={onBack} />
    <div class="spacer" />
    {#if !isView}
        <div class="generate-link-toggle">
            <Zinput type="toggle" bind:value={generateLinkAfterSave} label="Gerar link após salvar" />
        </div>
        <Zbutton color="secondary" icon={faFloppyDisk} label="Salvar preenchimento" loading={saving} disabled={saving} on:click={onSaveDraft} />
        <Zbutton color="primary" icon={faCheck} label="Concluir" loading={saving} disabled={saving} on:click={onSubmit} />
    {:else}
        {#if formStatus === COMPLETED}
            <Zbutton color="secondary" icon={faFloppyDisk} label="Voltar para preenchendo" on:click={backToFilling} />
        {/if}
        <Zbutton color="primary" icon={faPrint} label="Imprimir" loading={show} disabled={show} on:click={printCurrentForm} />
    {/if}
</Zact>
<Zframe class="h-space form-v2-formeditor_zframe">
    <div class="fill-layout">
        <div class="fill-sidebar">
            <ZentitySelect fonte="usuario" class="mb-2" label="Preenchido por" disabled={!!formV2FormId || isView} bind:value={selectedAttendant}></ZentitySelect>
            <ZentitySelect fonte="pessoa" class="mb-2" label="Cliente" disabled={isView} bind:value={selectedPerson}></ZentitySelect>
            <div class="sidebar-spacer" />
            <Zinput
                class="mb-2"
                label="Filtrar documento"
                type="multiselect"
                options={categoryOptions}
                bind:value={types}
                disabled={!!formV2FormId || isView}
                on:change={onTypesChange}
            />

            <div class="btn-group-vertical w-100">
                {#each filteredTemplates as tpl}
                    <input
                        class="btn-check"
                        type="radio"
                        name="template"
                        id="tpl_{tpl.formV2TemplateId}"
                        value={tpl.formV2TemplateId}
                        bind:group={selectedTemplateId}
                        disabled={!!formV2FormId || isView}
                        on:change={onTemplateChange}
                    />
                    <label for="tpl_{tpl.formV2TemplateId}" class="btn btn-outline-secondary text-start overflow-hidden">{tpl.title}</label>
                {/each}
                {#if filteredTemplates.length === 0}
                    <p class="sidebar-empty">Nenhum formulário ativo</p>
                {/if}
            </div>
            <div class="sidebar-spacer" />

            {#if invoiceRequired && !invoiceId && !billId}
                <ZentitySelect fonte="venda" label="Venda" disabled={!!formV2FormId || isView} params={{ personId: selectedPerson?.personId }} bind:value={invoice} />
            {/if}
        </div>

        <div class="fill-main">
            {#if formStatus === SIGNING}
                <div class="signature-notice pending">
                    <Fa icon={faClock} />
                    <span>Este documento está aguardando assinatura.</span>
                </div>
            {:else if formStatus === SIGNED}
                <div class="signature-notice signed">
                    <Fa icon={faCircleCheck} />
                    <span>Este documento foi assinado digitalmente.</span>
                </div>
            {/if}
            <FormFill
                bind:this={formFillRef}
                title={formTitle}
                {blocks}
                {headerFooter}
                {initialAnswers}
                {saving}
                hideActions={true}
                readOnlyAll={isView}
                context={resolveFillContext(formStatus, { person: selectedPerson, attendant: selectedAttendant, invoice, bill }, contextSnapshot)}
                formId={formV2FormId}
            />
        </div>
    </div>
</Zframe>

{#if missingPersonFields.length > 0}
    <Popover title="Cadastro incompleto" width="560px" height="10px" on:close={() => (missingPersonFields = [])}>
        <div class="alert alert-warning m-3">
            {#if missingPersonFields.length === 1}
                Este formulário exige um dado do cadastro do cliente que ainda está em branco:
            {:else}
                Este formulário exige dados do cadastro do cliente que ainda estão em branco:
            {/if}
            <strong>{missingPersonFields.map((field) => REQUIRED_FIELD_LABELS[field] ?? field).join(', ')}</strong>.
            <br /><br />
            Você pode preencher o formulário normalmente, mas precisará completar o cadastro antes de salvar.
        </div>
    </Popover>
{/if}

<LoadSpinner show={show || saving} />

<style>
    .fill-layout {
        display: flex;
        gap: 0;
        min-height: 400px;
        width: 100%;
    }
    .fill-sidebar {
        min-width: 270px;
        flex: 1 0 320px;
        max-width: 320px;
        border-right: 1px solid #88888833;
        padding-right: 20px;
        margin-right: 20px;

        flex-direction: column;
        gap: 4px;
    }
    .sidebar-label {
        font-size: 0.75rem;
        font-weight: 600;
        color: #868e96;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        margin: 0 0 8px;
    }
    .sidebar-empty {
        font-size: 0.82rem;
        color: #adb5bd;
        margin: 0;
    }
    .sidebar-spacer {
        height: 16px;
    }
    .fill-main {
        flex: 1;
        min-width: 0;
        padding: 16px 0px 16px 0px;
    }
    .signature-notice {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 16px;
        padding: 10px 16px;
        border-radius: 6px;
        font-weight: 600;
        width: fit-content;
    }
    .signature-notice.signed {
        color: #2f9e44;
        background: #2f9e4422;
    }
    .signature-notice.pending {
        color: #e8590c;
        background: #e8590c22;
    }

    :global(.form-v2-formeditor_zframe) {
        width: 100%;
    }

    @media (max-width: 900px) {
        .fill-layout {
            flex-direction: column;
        }
        .fill-sidebar {
            width: 100%;
            border-right: none;
            border-bottom: 1px solid #ecedf0;
            padding-right: 0;
            margin-right: 0;
            padding-bottom: 16px;
            margin-bottom: 16px;
        }
    }
</style>
